import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AnalysisResult, PatternCategory, EnhancedPattern } from '@/types/trading-analysis-types';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Target } from 'lucide-react';

interface ComprehensiveAnalysisResultsProps {
  result: AnalysisResult;
}

export const ComprehensiveAnalysisResults: React.FC<ComprehensiveAnalysisResultsProps> = ({
  result
}) => {
  console.log('🔧 ComprehensiveAnalysisResults received data:', result);
  console.log('🔧 Result patterns count:', result?.patterns?.length);
  console.log('🔧 Result total patterns:', result?.totalPatterns);
  const getCategoryIcon = (category: PatternCategory) => {
    switch (category) {
      case 'HIGH_WIN':
      case 'MODERATE_WIN':
        return <TrendingUp className="h-4 w-4" />;
      case 'HIGH_LOSS':
      case 'MODERATE_LOSS':
        return <TrendingDown className="h-4 w-4" />;
      default:
        return <Minus className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category: PatternCategory) => {
    switch (category) {
      case 'HIGH_WIN':
        return 'bg-success text-success-foreground';
      case 'MODERATE_WIN':
        return 'bg-success/70 text-success-foreground';
      case 'BALANCED':
        return 'bg-muted text-muted-foreground';
      case 'MODERATE_LOSS':
        return 'bg-destructive/70 text-destructive-foreground';
      case 'HIGH_LOSS':
        return 'bg-destructive text-destructive-foreground';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'strong_buy':
      case 'buy':
        return 'text-success';
      case 'reverse':
      case 'avoid':
        return 'text-destructive';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatAction = (action: string) => {
    return action.replace('_', ' ').toUpperCase();
  };

  const PatternCard: React.FC<{ pattern: EnhancedPattern }> = ({ pattern }) => (
    <Card className="border border-border/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {pattern.classification && getCategoryIcon(pattern.classification.category)}
            <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
              {pattern.pattern}
            </code>
          </div>
          {pattern.classification && (
            <Badge className={getCategoryColor(pattern.classification.category)}>
              {pattern.classification.category.replace('_', ' ')}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Success Rate:</span>
            <div className="font-semibold">{pattern.successRate.toFixed(1)}%</div>
          </div>
          <div>
            <span className="text-muted-foreground">Frequency:</span>
            <div className="font-semibold">{pattern.frequency}</div>
          </div>
          <div>
            <span className="text-muted-foreground">Wins/Losses:</span>
            <div className="font-semibold">{pattern.nextWinCount}/{pattern.nextLossCount}</div>
          </div>
          <div>
            <span className="text-muted-foreground">Confidence:</span>
            <div className="font-semibold">{pattern.confidenceScore}</div>
          </div>
        </div>
        
        {pattern.classification && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Recommendation:</span>
              <span className={`text-sm font-semibold ${getActionColor(pattern.classification.actionRecommendation)}`}>
                {formatAction(pattern.classification.actionRecommendation)}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              {pattern.classification.riskDescription}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const distribution = result.patternDistribution;
  const totalPatterns = result.totalPatterns;

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Total Patterns
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalPatterns}</div>
            <p className="text-sm text-muted-foreground">
              Processing time: {result.processingTime}ms
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-success">
              <TrendingUp className="h-5 w-5" />
              Win Opportunities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-success">
              {result.opportunityPatternsCount || result.opportunityPatterns?.length || 0}
            </div>
            <p className="text-sm text-muted-foreground">
              High-confidence win patterns
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Risk Warnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-destructive">
              {result.highRiskPatternsCount || result.highRiskPatterns?.length || 0}
            </div>
            <p className="text-sm text-muted-foreground">
              Patterns to avoid or reverse
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Pattern Distribution */}
      {distribution && (
        <Card>
          <CardHeader>
            <CardTitle>Pattern Distribution</CardTitle>
            <CardDescription>
              Breakdown of patterns by win/loss categories
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(distribution).map(([category, count]) => {
              const percentage = totalPatterns > 0 ? (count / totalPatterns) * 100 : 0;
              return (
                <div key={category} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      {getCategoryIcon(category as PatternCategory)}
                      <span className="font-medium">{category.replace('_', ' ')}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {count} ({percentage.toFixed(1)}%)
                    </div>
                  </div>
                  <Progress value={percentage} className="h-2" />
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* High-Risk Patterns (Loss Opportunities) */}
      {result.highRiskPatterns && result.highRiskPatterns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <TrendingDown className="h-5 w-5" />
              High-Risk Patterns (Avoid/Reverse)
            </CardTitle>
            <CardDescription>
              Patterns with high loss probability - consider avoiding or reversing positions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                These patterns show high loss rates. Consider avoiding trades or taking reverse positions when these patterns appear.
              </AlertDescription>
            </Alert>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {result.highRiskPatterns.slice(0, 6).map((pattern, index) => (
                <PatternCard key={index} pattern={pattern} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Win Opportunities */}
      {result.opportunityPatterns && result.opportunityPatterns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-success">
              <TrendingUp className="h-5 w-5" />
              Win Opportunities
            </CardTitle>
            <CardDescription>
              High-confidence patterns with favorable win rates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {result.opportunityPatterns.slice(0, 6).map((pattern, index) => (
                <PatternCard key={index} pattern={pattern} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Patterns */}
      <Card>
        <CardHeader>
          <CardTitle>All Patterns</CardTitle>
          <CardDescription>
            Complete analysis of all detected patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {result.patterns.slice(0, 12).map((pattern, index) => (
              <PatternCard key={index} pattern={pattern} />
            ))}
          </div>
          {result.patterns.length > 12 && (
            <div className="mt-4 text-center text-sm text-muted-foreground">
              Showing 12 of {result.patterns.length} patterns
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};